import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AttachmentPreviews,
  RichTextEditor,
  isDocEmpty,
  usePostAttachments,
  type TiptapDoc,
} from '@onesource/components';
import {
  CommunityTagType,
  CreatePostSchema,
  MAX_NON_VIDEO_ATTACHMENT_BYTES,
  formatBytes,
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  POST_TYPE,
  POST_TYPE_DEFINITIONS,
  PostStatus,
  type TAvailableTag,
  type TCreatePost,
  type TMentionUser,
  type TPostWithAuthor,
  type TUpdatePost,
} from '@onesource/schemas';
import { fetchSpaceMentionUsers, useCreatePost, useGetAvailableTags, useUpdatePost } from '@onesource/services';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { toLocalInput } from '../utils/datetime';
import { DateTimeField, DateTimePickerPanel } from './date-time-field';
import { FLUID_TYPE } from './fluid-type';
import { PinToggle } from './pin-toggle';
import { PostEditorSurface } from './post-editor-surface';
import { PostTagSelector } from './post-tag-selector';
import { PostTypeSelect } from './post-type-select';
import { ScheduleToggle } from './schedule-toggle';
import { SelectedTagChips } from './selected-tag-chips';

interface PostComposerProps {
  /** Feed sub-space id — drives the create mutation and available-tags query. */
  spaceId: string;
  /** Top-level container id — mention lookups are scoped to it. */
  containerSpaceId: string;
  /** Space managers get scheduling, polls and admin-only post types. */
  canManageSpace: boolean;
  /** Whether the form is active — expanded inline (md+) or open in the sheet (phone). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided the composer edits this post; otherwise it creates a new one. */
  post?: TPostWithAuthor;
  /** Phone bottom-sheet header. */
  sheetTitle: string;
  sheetSubtitle: string;
  /** Called after a successful create / update (in addition to closing). */
  onSubmitted?: () => void;
}

/** Non-empty, trimmed poll option texts. */
const cleanPollOptions = (options: string[]): string[] => options.map(text => text.trim()).filter(Boolean);

/**
 * Post Composer — the single form used to both create a new post and edit an
 * existing one. Passing `post` switches it to edit mode: every field is seeded
 * from the post, the submit button saves via the update endpoint, and it stays
 * disabled until something actually changes. Layout, controls and the phone
 * bottom-sheet are identical in both modes.
 *
 * Renders in two hosts driven by breakpoint: an inline column from md up (the
 * parent decides where it sits — the feed's composer card or in place of a post
 * card's body), and a full-height bottom sheet on phones.
 */
export const PostComposer = ({
  spaceId,
  containerSpaceId,
  canManageSpace,
  open,
  onOpenChange,
  post,
  sheetTitle,
  sheetSubtitle,
  onSubmitted,
}: PostComposerProps) => {
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const isEdit = !!post;

  // Admin post types are a static taxonomy; non-managers only see public ones.
  const postTypes = POST_TYPE_DEFINITIONS.filter(d => canManageSpace || !d.adminOnly);

  // Managers can schedule at creation and reschedule an existing post — a future
  // time moves a published or scheduled post back to SCHEDULED (hidden from the
  // feed until then). Posts still processing media (or removed) are excluded so
  // an edit never clears their hold by sending a schedule.
  const canSchedule =
    canManageSpace && (!post || post.status === PostStatus.PUBLISHED || post.status === PostStatus.SCHEDULED);

  // Parsed body of the post being edited — the seed for the rich-text doc and
  // the baseline for change detection.
  const initialBodyDoc = useMemo<TiptapDoc | null>(() => {
    if (!post) {
      return null;
    }
    try {
      return JSON.parse(post.body) as TiptapDoc;
    } catch {
      return null;
    }
  }, [post]);

  // The rich-text doc and file uploads are local state synced into the form.
  const [postBody, setPostBody] = useState<TiptapDoc | null>(initialBodyDoc);
  const [selectedTags, setSelectedTags] = useState<TAvailableTag[]>([]);
  // Members can attach videos, under a per-file size cap and a maximum clip
  // length; space managers have neither. members-api enforces the real numbers
  // at presign — these only drive what the picker blocks locally.
  const attachmentsManager = usePostAttachments(post?.attachments, { canManage: canManageSpace });
  // Spell the ceilings out on the attach button so a member learns them before
  // picking a file rather than from a rejection afterwards.
  const attachmentHint = [
    // Images and PDFs carry a lower, tier-independent ceiling than videos, and
    // it is measured after compression — see MAX_NON_VIDEO_ATTACHMENT_BYTES.
    `Images and PDFs up to ${formatBytes(MAX_NON_VIDEO_ATTACHMENT_BYTES)}`,
    attachmentsManager.maxBytes === null ? 'videos' : `videos up to ${formatBytes(attachmentsManager.maxBytes)}`,
    attachmentsManager.maxDurationSec !== null && `${attachmentsManager.maxDurationSec}s max`,
  ]
    .filter(Boolean)
    .join(' · ');
  // Admin-only scheduling. `scheduleAt` is a datetime-local string ('' = none).
  const [scheduleAt, setScheduleAt] = useState(post?.scheduledAt ? toLocalInput(new Date(post.scheduledAt)) : '');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const scheduleAnchorRef = useRef<HTMLDivElement>(null);
  // Admin-only poll builder.
  const [pollEnabled, setPollEnabled] = useState(!!post?.poll);
  const [pollOptions, setPollOptions] = useState<string[]>(post?.poll ? post.poll.options.map(o => o.text) : ['', '']);
  const [pollAllowMultiple, setPollAllowMultiple] = useState(post?.poll?.allowMultiple ?? false);
  const [pollClosesAt, setPollClosesAt] = useState(
    post?.poll?.closesAt ? toLocalInput(new Date(post.poll.closesAt)) : '',
  );

  const createPost = useCreatePost(spaceId);
  const updatePost = useUpdatePost(post?.id ?? '');
  const mutation = isEdit ? updatePost : createPost;

  const fetchMentionUsers = useCallback(
    (query: string): Promise<TMentionUser[]> => fetchSpaceMentionUsers(containerSpaceId, query),
    [containerSpaceId],
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<TCreatePost>({
    resolver: zodResolver(CreatePostSchema),
    mode: 'onChange',
    defaultValues: {
      body: post?.body ?? '',
      title: post?.title ?? undefined,
      postType: post?.postType ?? POST_TYPE.QUESTION,
      attachments: post?.attachments && post.attachments.length > 0 ? post.attachments : undefined,
      isPinned: post?.isPinned ?? false,
    },
  });

  // Lazily load the container's available tags while the form is open.
  const { data: availableTags, isLoading: isLoadingTags } = useGetAvailableTags(spaceId, { enabled: open });

  // The post's current tags resolved back to selectable options — used to
  // pre-select on open and as the change-detection baseline. Match by source id
  // (question tags carry the registry id in `value`; subspace/lesson tags carry
  // the source entity id in `refId`) — labels aren't unique.
  const initialSelectedTags = useMemo<TAvailableTag[]>(() => {
    if (!post || !availableTags) {
      return [];
    }
    return (post.tags ?? [])
      .map(ref =>
        availableTags.find(option => {
          if (option.type !== ref.type) {
            return false;
          }
          if (ref.type === CommunityTagType.QUESTION) {
            return option.value === ref.id;
          }
          return ref.refId !== null && option.value === ref.refId;
        }),
      )
      .filter((option): option is TAvailableTag => !!option);
  }, [post, availableTags]);

  // Re-seed every field from the post each time the edit form opens (or the
  // post changes). Keyed on open + post id so typing doesn't reset the form.
  useEffect(() => {
    if (!open || !post) {
      return;
    }
    reset({
      body: post.body,
      title: post.title ?? undefined,
      postType: post.postType ?? POST_TYPE.QUESTION,
      isPinned: post.isPinned,
    });
    setPostBody(initialBodyDoc);
    setScheduleAt(post.scheduledAt ? toLocalInput(new Date(post.scheduledAt)) : '');
    setPollEnabled(!!post.poll);
    setPollOptions(post.poll ? post.poll.options.map(o => o.text) : ['', '']);
    setPollAllowMultiple(post.poll?.allowMultiple ?? false);
    setPollClosesAt(post.poll?.closesAt ? toLocalInput(new Date(post.poll.closesAt)) : '');
    attachmentsManager.reset(post.attachments);
    // attachmentsManager / reset are stable; re-seeding on every keystroke is
    // exactly what we must avoid, so intentionally only track open + post id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post?.id]);

  useEffect(() => {
    if (!open || !post || !availableTags) {
      return;
    }
    setSelectedTags(initialSelectedTags);
  }, [open, post, availableTags, initialSelectedTags]);

  // Sync the rich-text doc into `body`. An empty editor still serializes to a
  // blank paragraph, so gate on isDocEmpty to keep `body` empty and invalid.
  useEffect(() => {
    setValue('body', isDocEmpty(postBody) ? '' : JSON.stringify(postBody), { shouldValidate: true });
  }, [postBody, setValue]);

  // Sync uploaded files into `attachments`.
  const composerAttachments = attachmentsManager.attachments;
  useEffect(() => {
    setValue('attachments', composerAttachments.length > 0 ? composerAttachments : undefined, { shouldValidate: true });
  }, [composerAttachments, setValue]);

  // Sync the poll builder into `poll` for validation (min 2 non-empty options).
  // Empty option rows are dropped. In edit mode the update payload is built
  // separately (to protect existing votes); this only drives form validity.
  useEffect(() => {
    if (!pollEnabled) {
      setValue('poll', undefined, { shouldValidate: true });
      return;
    }
    setValue(
      'poll',
      {
        options: cleanPollOptions(pollOptions).map(text => ({ text })),
        allowMultiple: pollAllowMultiple,
        closesAt: pollClosesAt ? new Date(pollClosesAt) : undefined,
      },
      { shouldValidate: true },
    );
  }, [pollEnabled, pollOptions, pollAllowMultiple, pollClosesAt, setValue]);

  const updatePollOption = (index: number, value: string) => {
    setPollOptions(prev => prev.map((opt, i) => (i === index ? value : opt)));
  };
  const addPollOption = () => {
    setPollOptions(prev => (prev.length >= POLL_MAX_OPTIONS ? prev : [...prev, '']));
  };
  const removePollOption = (index: number) => {
    setPollOptions(prev => (prev.length <= POLL_MIN_OPTIONS ? prev : prev.filter((_, i) => i !== index)));
  };

  const clearComposer = useCallback(() => {
    reset({ body: '', postType: POST_TYPE.QUESTION, attachments: undefined, poll: undefined, isPinned: false });
    setPostBody(null);
    setSelectedTags([]);
    attachmentsManager.clear();
    setScheduleAt('');
    setCalendarOpen(false);
    setPollEnabled(false);
    setPollOptions(['', '']);
    setPollAllowMultiple(false);
    setPollClosesAt('');
    // attachmentsManager is stable; only reset is a real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setCalendarOpen(false);
    // Create keeps a fresh sheet next time; edit re-seeds from the post on open.
    if (!isEdit) {
      clearComposer();
    }
  }, [onOpenChange, isEdit, clearComposer]);

  // A future schedule instant (managers only) — reflected in the chip, button
  // label and the create payload.
  const scheduledDate = canSchedule && scheduleAt ? new Date(scheduleAt) : undefined;
  const isScheduledFuture = !!scheduledDate && scheduledDate.getTime() > Date.now();

  // Change detection — only meaningful while editing. Everything syncs via
  // setValue (which never marks the form dirty), so compare each editable piece
  // against the post to keep Save disabled until something actually changed.
  const watchedTitle = watch('title');
  const watchedPostType = watch('postType');
  const watchedIsPinned = watch('isPinned');
  const hasChanges = useMemo(() => {
    if (!post) {
      return true;
    }
    if ((watchedTitle ?? '').trim() !== (post.title ?? '').trim()) {
      return true;
    }
    if (JSON.stringify(postBody) !== JSON.stringify(initialBodyDoc)) {
      return true;
    }
    const currentUrls = attachmentsManager.attachments.map(a => a.url);
    const originalUrls = (post.attachments ?? []).map(a => a.url);
    if (currentUrls.length !== originalUrls.length || currentUrls.some((url, i) => url !== originalUrls[i])) {
      return true;
    }
    if (
      selectedTags.length !== initialSelectedTags.length ||
      selectedTags.some(tag => !initialSelectedTags.some(t => t.type === tag.type && t.value === tag.value))
    ) {
      return true;
    }
    if ((watchedPostType ?? undefined) !== (post.postType ?? undefined)) {
      return true;
    }
    if ((watchedIsPinned ?? post.isPinned) !== post.isPinned) {
      return true;
    }
    if (canSchedule && scheduleAt !== (post.scheduledAt ? toLocalInput(new Date(post.scheduledAt)) : '')) {
      return true;
    }
    // Poll changes.
    if (pollEnabled !== !!post.poll) {
      return true;
    }
    if (pollEnabled && post.poll) {
      const originalOptions = post.poll.options.map(o => o.text);
      const currentOptions = cleanPollOptions(pollOptions);
      if (currentOptions.length !== originalOptions.length || currentOptions.some((t, i) => t !== originalOptions[i])) {
        return true;
      }
      if (pollAllowMultiple !== post.poll.allowMultiple) {
        return true;
      }
      if (pollClosesAt !== (post.poll.closesAt ? toLocalInput(new Date(post.poll.closesAt)) : '')) {
        return true;
      }
    }
    return false;
  }, [
    post,
    watchedTitle,
    watchedPostType,
    watchedIsPinned,
    postBody,
    initialBodyDoc,
    attachmentsManager.attachments,
    selectedTags,
    initialSelectedTags,
    canSchedule,
    scheduleAt,
    pollEnabled,
    pollOptions,
    pollAllowMultiple,
    pollClosesAt,
  ]);

  const onSubmit = async (data: TCreatePost) => {
    const tags = selectedTags.map(tag => ({ type: tag.type, value: tag.value }));
    try {
      if (!post) {
        await createPost.mutateAsync({
          ...data,
          scheduledAt: scheduledDate,
          tags: tags.length > 0 ? tags : undefined,
        });
      } else {
        const payload: TUpdatePost = {
          title: data.title?.trim() || undefined,
          body: data.body,
          postType: data.postType,
          isPinned: data.isPinned,
          attachments: attachmentsManager.attachments,
          tags,
        };
        if (canSchedule) {
          // A cleared schedule publishes now (null); a future instant reschedules.
          payload.scheduledAt = scheduleAt ? new Date(scheduleAt) : null;
        }
        // Poll: only send the full builder when the option set changed (which
        // resets votes). An options-unchanged edit just moves the close time.
        const optionSetChanged =
          pollEnabled !== !!post.poll ||
          (pollEnabled &&
            !!post.poll &&
            (() => {
              const originalOptions = post.poll.options.map(o => o.text);
              const currentOptions = cleanPollOptions(pollOptions);
              return (
                currentOptions.length !== originalOptions.length ||
                currentOptions.some((t, i) => t !== originalOptions[i]) ||
                pollAllowMultiple !== post.poll.allowMultiple
              );
            })());
        if (optionSetChanged) {
          payload.poll = pollEnabled
            ? {
                options: cleanPollOptions(pollOptions).map(text => ({ text })),
                allowMultiple: pollAllowMultiple,
                closesAt: pollClosesAt ? new Date(pollClosesAt) : undefined,
              }
            : null;
        } else if (pollEnabled && post.poll) {
          const originalClose = post.poll.closesAt ? toLocalInput(new Date(post.poll.closesAt)) : '';
          if (pollClosesAt !== originalClose) {
            payload.pollClosesAt = pollClosesAt ? new Date(pollClosesAt) : null;
          }
        }
        await updatePost.mutateAsync(payload);
      }
      onSubmitted?.();
      handleClose();
    } catch {
      // The mutation surfaces its own error state.
    }
  };

  const canSubmit = isValid && !attachmentsManager.isUploading && !mutation.isPending && (!isEdit || hasChanges);

  let submitLabel = isEdit ? 'Save' : 'Post';
  if (mutation.isPending) {
    submitLabel = isEdit ? 'Saving...' : isScheduledFuture ? 'Scheduling...' : 'Posting...';
  } else if (!isEdit && isScheduledFuture) {
    submitLabel = 'Schedule';
  }

  const fields = (
    <>
      <Controller
        name="title"
        control={control}
        render={({ field, fieldState }) => (
          <PostEditorSurface
            title={field.value ?? ''}
            onTitleChange={field.onChange}
            titleError={fieldState.error?.message}
            editorMinHeight={isBelowMd ? '30dvh' : undefined}
            editorMaxHeight={isBelowMd ? '38dvh' : undefined}
            titleAction={
              // The phone sheet has its own close button in the header.
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ mr: '-6px', display: { xs: 'none', md: 'inline-flex' } }}
              >
                <CloseIcon sx={{ fontSize: 18, color: '#808080' }} />
              </IconButton>
            }
          >
            <RichTextEditor
              value={postBody}
              onChange={setPostBody}
              onFetchMentionUsers={fetchMentionUsers}
              placeholder={isEdit ? 'Edit your post...' : "What's on your mind?"}
              toolbarActions={
                <>
                  <Tooltip title={attachmentHint}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Add attachments"
                        onClick={attachmentsManager.openFilePicker}
                        disabled={attachmentsManager.atLimit}
                        sx={{ '&:hover': { bgcolor: '#e2dad7' } }}
                      >
                        <AttachFileOutlinedIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {canManageSpace && (
                    <Tooltip title="Add a poll">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Add a poll"
                          onClick={() => setPollEnabled(prev => !prev)}
                          color={pollEnabled ? 'primary' : 'default'}
                          sx={{ '&:hover': { bgcolor: '#e2dad7' } }}
                        >
                          <PollOutlinedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              }
            />
          </PostEditorSurface>
        )}
      />

      {/* Selected tags — shown outside the input */}
      <SelectedTagChips
        tags={selectedTags}
        onRemove={tag => setSelectedTags(prev => prev.filter(t => !(t.type === tag.type && t.value === tag.value)))}
      />

      <AttachmentPreviews items={attachmentsManager.items} onRemove={attachmentsManager.removeItem} />

      {attachmentsManager.error && (
        <Alert severity="warning" onClose={() => attachmentsManager.setError(null)} sx={{ py: 0 }}>
          {attachmentsManager.error}
        </Alert>
      )}

      <input
        ref={attachmentsManager.fileInputRef}
        type="file"
        accept={attachmentsManager.accept}
        multiple
        style={{ display: 'none' }}
        onChange={attachmentsManager.handleFileSelect}
      />

      {/* Admin-only poll builder */}
      {canManageSpace && pollEnabled && (
        <Box
          sx={{
            '@keyframes sectionIn': {
              from: { opacity: 0, transform: 'translateY(-4px)' },
              to: { opacity: 1, transform: 'none' },
            },
            animation: 'sectionIn 0.18s ease-out',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            p: { xs: 1.5, md: 2 },
            borderRadius: '12px',
            border: '1px solid #ececec',
            bgcolor: '#fcfcfc',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PollOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 14, fontWeight: 700, color: '#333' }}>
                Create a poll
              </Typography>
            </Box>
            <Tooltip title="Remove poll">
              <IconButton size="small" onClick={() => setPollEnabled(false)}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {pollOptions.map((option, index) => (
            // oxlint-disable-next-line react/no-array-index-key -- options have no stable id
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#f0f0f0',
                  color: '#808080',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder={`Option ${index + 1}`}
                value={option}
                onChange={e => updatePollOption(index, e.target.value)}
                inputProps={{ maxLength: 200 }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'common.white' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                }}
              />
              {pollOptions.length > POLL_MIN_OPTIONS && (
                <Tooltip title="Remove option">
                  <IconButton size="small" onClick={() => removePollOption(index)}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          {pollOptions.length < POLL_MAX_OPTIONS && (
            <Box
              role="button"
              tabIndex={0}
              onClick={addPollOption}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                alignSelf: 'flex-start',
                cursor: 'pointer',
                color: '#666',
                '&:hover': {
                  color: '#333',
                  '& .add-option-tile': { borderColor: 'grey.400', bgcolor: '#f7f7f7' },
                },
              }}
            >
              <Box
                className="add-option-tile"
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'border-color 0.15s ease, background-color 0.15s ease',
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13, fontWeight: 600 }}>
                Add option
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              pt: 1.25,
              borderTop: '1px solid #f0f0f0',
            }}
          >
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={pollAllowMultiple}
                  onChange={e => setPollAllowMultiple(e.target.checked)}
                  sx={{ mr: 0.75 }}
                />
              }
              label={
                <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13, color: '#555' }}>
                  Allow multiple choices
                </Typography>
              }
            />
            <DateTimeField
              label="Closes at"
              placeholder="Closes at (optional)"
              value={pollClosesAt}
              onChange={setPollClosesAt}
            />
          </Box>
        </Box>
      )}

      {/* Surface the chosen schedule so it's visible and clearable. */}
      {isScheduledFuture && scheduledDate && (
        <Chip
          icon={<ScheduleOutlinedIcon sx={{ fontSize: 15 }} />}
          label={`Scheduled for ${format(scheduledDate, "MMM d 'at' h:mm a")}`}
          size="small"
          onClick={() => setCalendarOpen(true)}
          onDelete={() => setScheduleAt('')}
          sx={{
            '@keyframes chipIn': {
              from: { opacity: 0, transform: 'scale(0.9)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
            animation: 'chipIn 0.15s ease-out',
            alignSelf: 'flex-start',
            height: 26,
            borderRadius: '13px',
            bgcolor: '#FCF3E0',
            color: '#B8860B',
            fontFamily: 'Figtree, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            '& .MuiChip-icon': { color: '#B8860B' },
            '& .MuiChip-deleteIcon': { color: '#B8860B99', '&:hover': { color: '#B8860B' } },
            '&:hover': { bgcolor: '#F9EBCE' },
          }}
        />
      )}
    </>
  );

  const footer = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: 1,
        pt: '12px',
        borderTop: '1px solid #f0f0f0',
      }}
    >
      {/* Attach/poll live in the editor toolbar; this row keeps the selects. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Controller
          name="postType"
          control={control}
          render={({ field }) => <PostTypeSelect value={field.value} onChange={field.onChange} options={postTypes} />}
        />
        <PostTagSelector
          options={availableTags ?? []}
          value={selectedTags}
          onChange={setSelectedTags}
          loading={isLoadingTags}
        />
        {/* Schedule sits immediately to the left of Pin — both are neutral
            pills that open a picker / toggle state without reading as primary. */}
        {canSchedule && (
          <Box ref={scheduleAnchorRef} sx={{ display: 'inline-flex' }}>
            <ScheduleToggle scheduled={isScheduledFuture} onClick={() => setCalendarOpen(true)} />
          </Box>
        )}
        {canManageSpace && (
          <Controller
            name="isPinned"
            control={control}
            render={({ field }) => <PinToggle pinned={!!field.value} onChange={field.onChange} />}
          />
        )}

        {/* The Schedule pill opens the picker directly — "post now" is just the
            panel's clear action. */}
        {canSchedule && calendarOpen && (
          <Popover
            open
            anchorEl={scheduleAnchorRef.current}
            onClose={() => setCalendarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } } }}
          >
            <DateTimePickerPanel
              title={isEdit ? 'Publish at' : 'Schedule post'}
              description="Hidden from the feed until this time."
              value={scheduleAt}
              applyLabel={isEdit ? 'Reschedule' : 'Schedule'}
              onApply={next => {
                setScheduleAt(next);
                setCalendarOpen(false);
              }}
              onClear={() => {
                setScheduleAt('');
                setCalendarOpen(false);
              }}
            />
          </Popover>
        )}
      </Box>
      {/* Full-width on phones; inline from sm up. Only the submit button reads as
          primary — every other control is a neutral pill. */}
      <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!canSubmit}
          startIcon={!isEdit && isScheduledFuture ? <ScheduleOutlinedIcon sx={{ fontSize: 15 }} /> : undefined}
          sx={{
            // Primary CTA — solid primary background when active. 34px tall,
            // 10px radius; the theme's MuiButton root forces minHeight 40px, so
            // override it here too.
            borderRadius: '10px',
            height: 34,
            minHeight: 34,
            textTransform: 'none',
            fontFamily: 'Figtree, sans-serif',
            fontWeight: 600,
            px: 3,
            boxShadow: 'none',
            flex: { xs: 1, sm: '0 0 auto' },
            // The theme paints contained-primary with a main->light gradient;
            // use a solid primary background instead. :not(.Mui-disabled) keeps
            // the grey disabled state intact.
            '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
              background: theme => theme.palette.primary.main,
              color: theme => theme.palette.primary.contrastText,
              '&:hover': { background: theme => theme.palette.primary.dark, boxShadow: 'none' },
            },
            // The theme forces disabled buttons to grey[400] with !important
            // (components.ts MuiButton root, same selector specificity), so win
            // via `&&` (doubled specificity) plus !important.
            '&&.Mui-disabled': { background: '#E2E2E2 !important', color: '#9E9E9E' },
          }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Inline host (md+): a fade-and-settle column the parent places in its
          card / in place of the post body. Phones use the sheet below. */}
      {open && !isBelowMd && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            '@keyframes composerIn': {
              from: { opacity: 0, transform: 'translateY(-6px) scale(0.99)' },
              to: { opacity: 1, transform: 'none' },
            },
            animation: 'composerIn 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        >
          {fields}
          {footer}
        </Box>
      )}

      {/* Phone host — a full-height bottom sheet. */}
      {isBelowMd && (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          onOpen={() => undefined}
          disableSwipeToOpen
          PaperProps={{
            sx: { borderRadius: '20px 20px 0 0', maxHeight: '92dvh', display: 'flex', flexDirection: 'column' },
          }}
        >
          {/* Grab handle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px' }}>
            <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'grey.300' }} />
          </Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 1.25, pb: 1.5 }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: FLUID_TYPE.title,
                  lineHeight: 1.3,
                  fontWeight: 800,
                  color: '#1a1a1a',
                }}
              >
                {sheetTitle}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: FLUID_TYPE.caption,
                  lineHeight: 1.5,
                  color: '#808080',
                }}
              >
                {sheetSubtitle}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Close composer"
              sx={{ bgcolor: '#f4f4f4', color: '#555', '&:hover': { bgcolor: '#ececec' } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              px: 2,
              pb: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              // A scrolling flex column shrinks its children to fit instead of
              // overflowing — keep every field full height.
              '& > *': { flexShrink: 0 },
            }}
          >
            {fields}
          </Box>
          {/* Pinned action footer, clear of the home indicator */}
          <Box sx={{ px: 2, pb: 'calc(10px + env(safe-area-inset-bottom))', flexShrink: 0 }}>{footer}</Box>
        </SwipeableDrawer>
      )}
    </>
  );
};
